import { useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import { comments } from '@/mocks/homeData';
import type { CommentItem } from '@/mocks/homeData';

export default function DiscussSection() {
  const [list, setList] = useState<CommentItem[]>(comments);
  const [draft, setDraft] = useState('');
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [hint, setHint] = useState('');

  const handleLike = (id: string) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handlePublish = () => {
    const value = draft.trim();
    if (!value) {
      setHint('先写点你的判断，再发布出去。');
      return;
    }
    if (value.length > 200) {
      setHint('评论要控制在 200 字以内，太长可以先发一部分。');
      return;
    }
    const next: CommentItem = {
      id: `local-${list.length + 1}`,
      user: '我（本机草稿）',
      avatarText: '我',
      time: '刚刚',
      content: value,
      likes: 0,
      replies: 0,
      tag: '待归类',
    };
    setList((prev) => [next, ...prev]);
    setDraft('');
    setHint('已加入本机的对线区列表（演示页面不会真的提交）。');
  };

  return (
    <section id="discuss" className="w-full bg-background-100 py-16 md:py-24 scroll-mt-20">
      <div className="w-full px-4 md:px-10">
        <SectionHeading
          eyebrow="Discussion Zone"
          title="对线区：证据摆在这，观点也摆在这"
          description="每一条评论都可以直接引用归档编号。有人说证据对不上，就把对应卡片的时间码贴上来，比空口争论有用得多。"
          right={
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-foreground-600">
                <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center text-lg">
                  <i className="ri-chat-3-line" />
                </span>
                今日新增 1,284 条
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground-600">
                <span className="w-9 h-9 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center text-lg">
                  <i className="ri-thumb-up-line" />
                </span>
                有用投票 9,610
              </div>
            </div>
          }
        />

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 flex flex-col gap-4">
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm">
                  我
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-950">写一条对线记录</p>
                  <p className="text-xs text-foreground-500">
                    带上证据编号，观点更容易被采纳
                  </p>
                </div>
              </div>
              <textarea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                maxLength={500}
                rows={3}
                placeholder="例如：case-01 第 14 分钟的截图和 09-06 的文件日期对不上……"
                className="mt-4 w-full rounded-md border border-background-300 bg-background-50 px-3 py-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 resize-none"
              />
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <span className="text-xs text-foreground-500">
                  {hint || `还可以输入 ${500 - draft.length} 个字符`}
                </span>
                <button
                  type="button"
                  onClick={handlePublish}
                  className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-send-plane-2-line" />
                  发布到对线区
                </button>
              </div>
            </div>

            {list.map((item) => {
              const isLiked = Boolean(liked[item.id]);
              return (
                <article
                  key={item.id}
                  className="rounded-lg border border-background-200/70 bg-background-50 p-4 md:p-5"
                >
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 shrink-0 rounded-full bg-secondary-200 text-secondary-900 flex items-center justify-center text-sm font-medium">
                      {item.avatarText}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <strong className="text-sm font-medium text-foreground-950">
                          {item.user}
                        </strong>
                        <span className="text-xs text-foreground-400">{item.time}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-md bg-primary-100 text-primary-800 whitespace-nowrap">
                          {item.tag}
                        </span>
                      </div>
                      <p className="mt-2 text-sm md:text-base leading-relaxed text-foreground-700">
                        {item.content}
                      </p>
                      <div className="mt-3 flex flex-wrap items-center gap-4">
                        <button
                          type="button"
                          onClick={() => handleLike(item.id)}
                          className={`flex items-center gap-1.5 text-xs cursor-pointer transition-colors ${
                            isLiked ? 'text-accent-600' : 'text-foreground-500 hover:text-accent-600'
                          }`}
                        >
                          <i className={isLiked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} />
                          {item.likes + (isLiked ? 1 : 0)}
                        </button>
                        <button
                          type="button"
                          className="flex items-center gap-1.5 text-xs text-foreground-500 hover:text-primary-700 transition-colors cursor-pointer"
                        >
                          <i className="ri-chat-1-line" />
                          {item.replies} 条回复
                        </button>
                        <button
                          type="button"
                          className="flex items-center gap-1.5 text-xs text-foreground-500 hover:text-primary-700 transition-colors cursor-pointer"
                        >
                          <i className="ri-bookmark-line" />
                          收藏该条
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <aside className="lg:col-span-4 flex flex-col gap-4">
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
              <h3 className="text-base font-semibold text-foreground-950">对线礼仪</h3>
              <ul className="mt-4 flex flex-col gap-3">
                {[
                  '引用证据时带上卡片编号，方便别人核对',
                  '来源不明的截图会被标记，别当成实锤',
                  '聊到当事人隐私的地方，建议打码后再发',
                  '不同观点可以吵，但别把结论当事实',
                ].map((tip) => (
                  <li key={tip} className="flex items-start gap-2 text-sm text-foreground-600">
                    <i className="ri-checkbox-circle-line text-primary-500 mt-0.5" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
              <span className="text-xs tracking-[0.2em] text-accent-700">投稿通道</span>
              <p className="mt-3 text-base font-semibold text-foreground-950">
                手里有新的实锤证据？
              </p>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                截图、录音、视频、聊天记录和文件都可以提交。入库前会先做一次哈希校验，原始文件不会公开修改。
              </p>
              <button
                type="button"
                className="mt-4 w-full px-4 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-upload-cloud-2-line" />
                提交线索（演示）
              </button>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}